import axios, { AxiosResponse, AxiosRequestConfig, RawAxiosRequestHeaders, ResponseType, InternalAxiosRequestConfig, AxiosHeaders } from "axios";
import { authentication, app } from "@microsoft/teams-js";
import i18n from '../i18n';
import { initializeTeamsApp } from '../helpers/teamsApp';

export class AxiosJWTDecorator {
    public async get<T = any, R = AxiosResponse<T>>(
        url: string,
        handleError: boolean = true,
        needAuthorizationHeader: boolean = true,
        isBlob: boolean = false,
        config?: AxiosRequestConfig,
    ): Promise<R> {
        try {
            if (needAuthorizationHeader) {
                config = await this.setupAuthorizationHeader(config);
            }
            if (isBlob){
                config!.responseType = 'blob';
            }
            var response: any = await axios.get(url, config);
            delete config!.responseType
            return response;

        } catch (error) {
            if (handleError) {
                this.handleError(error);
                throw error;
            }
            else {
                throw error;
            }
        }
    }

    public async delete<T = any, R = AxiosResponse<T>>(
        url: string,
        handleError: boolean = true,
        config?: AxiosRequestConfig
    ): Promise<R> {
        try {
            config = await this.setupAuthorizationHeader(config);
            return await axios.delete(url, config);
        } catch (error) {
            if (handleError) {
                this.handleError(error);
                throw error;
            }
            else {
                throw error;
            }
        }
    }

    public async post<T = any, R = AxiosResponse<T>>(
        url: string,
        data?: any,
        handleError: boolean = true,
        config?: AxiosRequestConfig
    ): Promise<R> {
        try {
            config = await this.setupAuthorizationHeader(config);
            return await axios.post(url, data, config);
        } catch (error) {
            if (handleError) {
                this.handleError(error);
                throw error;
            }
            else {
                throw error;
            }
        }
    }

    public async put<T = any, R = AxiosResponse<T>>(
        url: string,
        data?: any,
        handleError: boolean = true,
        config?: AxiosRequestConfig
    ): Promise<R> {
        try {
            config = await this.setupAuthorizationHeader(config);
            return await axios.put(url, data, config);
        } catch (error) {
            if (handleError) {
                this.handleError(error);
                throw error;
            }
            else {
                throw error;
            }
        }
    }

    public async patch<T = any, R = AxiosResponse<T>>(
        url: string,
        data?: any,
        handleError: boolean = true,
        config?: AxiosRequestConfig
    ): Promise<R> {
        try {
            config = await this.setupAuthorizationHeader(config);
            return await axios.patch(url, data, config);
        } catch (error) {
            if (handleError) {
                this.handleError(error);
                throw error;
            }
            else {
                throw error;
            }
        }
    }

  private handleError(error: any): void {
    if (error.hasOwnProperty("response")) {
      const errorStatus = error.response.status;
      if (errorStatus === 403) {
        window.location.href = `/errorpage/403?locale=${i18n.language}`;
      } else if (errorStatus === 401) {
        window.location.href = `/errorpage/401?locale=${i18n.language}`;
      } else {
          if (!window.location.search.includes('redirected=true')) {
              const newUrl = `/errorpage?locale=${i18n.language}?redirected=true`;
              window.location.href = newUrl;
          }
      }
    } else {
        if (!window.location.search.includes('redirected=true')) {
            const newUrl = `/errorpage?locale=${i18n.language}?redirected=true`;
            window.location.href = newUrl;
        }
    }
  }

    private async setupAuthorizationHeader(
        config?: AxiosRequestConfig
    ): Promise<AxiosRequestConfig> {
        await initializeTeamsApp();

        const authTokenRequest = {
            resources: []
        };

        return authentication.getAuthToken(authTokenRequest).then((token: string) => {
            if (!config) {
                config = axios.defaults.headers.common as AxiosRequestConfig;
            }
            if (!config.headers) {
                config.headers = {} as AxiosHeaders;
            }
            config.headers["Authorization"] = `Bearer ${token}`;
            config.headers["Accept-Language"] = i18n.language;
            return config;
        }).catch((error: string) => {
            console.log(error);
            console.error("Error from getAuthToken: ", error);
            window.location.href = `/signin?locale=${i18n.language}`;
            throw error;
        });
    }
}

const axiosJWTDecoratorInstance = new AxiosJWTDecorator();
export default axiosJWTDecoratorInstance;